import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { mpost, mdel, errMsg, eur, fmtDate, todayISO } from "@/manager/lib";
import { Card, Field, goldBtn } from "@/manager/ui";

const num = (v) => (v === "" || v == null ? null : Number(v));

function MoneyList({ title, items, onDelete, kind }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.14em] text-[#6b6a66] mb-2">{title}</p>
      {items.map((x) => (
        <p key={x.id} className="text-sm flex items-center gap-2 py-1 border-b border-[#f0ece4]" data-testid={`${kind}-${x.id}`}>
          <span className="flex-1">{fmtDate(x.date)} · {x.concept || x.method || x.note || (kind === "pay" ? "Cobro" : "Gasto")}</span>
          <span>{eur(x.amount)}</span><button onClick={() => onDelete(x)} className="text-rose-700"><Trash2 size={13} /></button>
        </p>
      ))}
      {!items.length && <p className="text-sm text-[#6b6a66]">—</p>}
    </div>
  );
}

export default function FinanceTab({ ev, save, reload }) {
  const f = ev.finance_summary;
  const [fin, setFin] = useState(ev.finance);
  const [pay, setPay] = useState({ amount: "", date: todayISO(), method: "" });
  const [exp, setExp] = useState({ concept: "", amount: "", date: todayISO() });
  useEffect(() => setFin(ev.finance), [ev.finance]);

  const addPay = async () => { try { await mpost("/payments", { event_id: ev.id, ...pay, amount: Number(pay.amount) }); setPay({ ...pay, amount: "" }); reload(); toast.success("Cobro registrado"); } catch (e) { toast.error(errMsg(e)); } };
  const addExp = async () => { try { await mpost("/expenses", { event_id: ev.id, ...exp, amount: Number(exp.amount) }); setExp({ ...exp, concept: "", amount: "" }); reload(); toast.success("Gasto registrado"); } catch (e) { toast.error(errMsg(e)); } };
  const tiles = [["Ingreso previsto", f.income_expected], ["Cobrado", f.collected], ["Pendiente", f.pending], ["Costes previstos", f.costs_expected],
    ["Costes reales", f.costs_real], ["Margen previsto", f.margin_expected], ["Margen real", f.margin_real]];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {tiles.map(([l, v]) => (
          <Card key={l} className="p-4"><p className={`font-display text-xl ${l.startsWith("Margen") && v < 0 ? "text-rose-600" : ""}`} data-testid={`fin-${l.toLowerCase().replace(/ /g, "-")}`}>{eur(v)}</p><p className="text-[10px] uppercase tracking-[0.14em] text-[#6b6a66]">{l}</p></Card>
        ))}
      </div>
      <p className="text-xs text-[#6b6a66]">Costes reales = gastos + personal confirmado ({eur(f.staff_cost)}){ev.kind === "externo" && " + desplazamiento"}. {f.fees > 0 && `Incluye fees de externos: ${eur(f.fees)}.`} El margen del evento no es el beneficio neto: los gastos fijos van aparte en Finanzas.</p>
      <Card className="p-5 flex flex-wrap gap-3 items-end">
        <Field label="Ingreso previsto (€)" className="w-40"><Input type="number" step="0.01" value={fin.income_expected ?? ""} onChange={(e) => setFin({ ...fin, income_expected: num(e.target.value) })} data-testid="fin-income-input" /></Field>
        <Field label="Costes previstos (€)" className="w-40"><Input type="number" step="0.01" value={fin.costs_expected ?? ""} onChange={(e) => setFin({ ...fin, costs_expected: num(e.target.value) })} data-testid="fin-costs-input" /></Field>
        <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={!!fin.vat_included} onCheckedChange={(v) => setFin({ ...fin, vat_included: v })} data-testid="fin-vat" /> Importes con IVA</label>
        <Button className={goldBtn} onClick={() => save({ finance: fin }, "Previsión guardada")} data-testid="fin-save">Guardar previsión</Button>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <Field label="Cobro (€)" className="w-28"><Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="pay-amount" /></Field>
            <Field label="Fecha" className="w-40"><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="pay-date" /></Field>
            <Field label="Método" className="w-32"><Input value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} placeholder="Señal, Bizum…" data-testid="pay-method" /></Field>
            <Button variant="outline" onClick={addPay} data-testid="pay-add">Añadir</Button>
          </div>
          <MoneyList title="Cobros" items={f.payments} kind="pay" onDelete={async (x) => { await mdel(`/payments/${x.id}`); reload(); }} />
        </Card>
        <Card className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <Field label="Concepto" className="w-36"><Input value={exp.concept} onChange={(e) => setExp({ ...exp, concept: e.target.value })} data-testid="exp-concept" /></Field>
            <Field label="Gasto (€)" className="w-28"><Input type="number" step="0.01" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} data-testid="exp-amount" /></Field>
            <Field label="Fecha" className="w-40"><Input type="date" value={exp.date} onChange={(e) => setExp({ ...exp, date: e.target.value })} data-testid="exp-date" /></Field>
            <Button variant="outline" onClick={addExp} data-testid="exp-add">Añadir</Button>
          </div>
          <MoneyList title="Gastos" items={f.expenses} kind="exp" onDelete={async (x) => { await mdel(`/expenses/${x.id}`); reload(); }} />
        </Card>
      </div>
    </div>
  );
}
